import { Switch } from "../../components/ui/switch";
import RevealPayout from "./RevealPayout";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Lock, Copy, Search, ChevronDown, FileText, FileSpreadsheet, Download } from "lucide-react";
import { useAuth, API } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";
import SettleConfirmDialog from "./SettleConfirmDialog";

export default function EventPnL({ eventId, onBack }) {
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [incomeForm, setIncomeForm] = useState({ description: "", amount: "", date: "", category: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ description: "", amount: "", date: "", category: "" });
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [allExpenses, setAllExpenses] = useState([]);
  const [expSearch, setExpSearch] = useState("");
  const [expExpanded, setExpExpanded] = useState(null);
  const [reach, setReach] = useState("");
  const [revCategories, setRevCategories] = useState([]);
  const [expCategories, setExpCategories] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [settling, setSettling] = useState(false);
  const { authAxios } = useAuth();
  const { getTenantSlug } = useTenant();

  useEffect(() => { fetchPnl(); fetchExpenses(); fetchCategories(); }, [eventId]);
  const fetchPnl = async () => {
    setLoading(true);
    try { const res = await authAxios.get(`/events/${eventId}/pnl`); setPnl(res.data); }
    catch { toast.error("Failed to load P&L"); }
    finally { setLoading(false); }
  };
  const fetchCategories = async () => {
    try { const res = await authAxios.get("/categories"); setRevCategories(res.data.filter(c => c.type === "revenue")); setExpCategories(res.data.filter(c => c.type === "expense")); } catch (err) { console.error("Request failed:", err?.message || err); }
  };
  const fetchExpenses = async () => {
    try { const res = await authAxios.get(`/events/${eventId}/expenses`); setAllExpenses(res.data); }
    catch (err) { console.error("Request failed:", err?.message || err); }
  };

  const handleAddIncome = async () => {
    if (!incomeForm.description || !incomeForm.amount) return toast.error("Description and amount required");
    try {
      await authAxios.post(`/events/${eventId}/income`, incomeForm);
      toast.success("Income added");
      setShowAdd(false); setIncomeForm({ description: "", amount: "", date: "" });
      fetchPnl();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDeleteIncome = async (id) => {
    try { await authAxios.delete(`/events/${eventId}/income/${id}`); toast.success("Removed"); fetchPnl(); }
    catch { toast.error("Error"); }
  };

  const handleAddDirectExpense = async () => {
    if (!expenseForm.description || !expenseForm.amount) return toast.error("Description and amount required");
    try {
      await authAxios.post(`/events/${eventId}/direct-expense`, expenseForm);
      toast.success("Expense added");
      setShowAddExpense(false); setExpenseForm({ description: "", amount: "", date: "" });
      fetchPnl(); fetchExpenses();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDeleteDirectExpense = async (id) => {
    try { await authAxios.delete(`/events/${eventId}/direct-expense/${id}`); toast.success("Removed"); fetchPnl(); fetchExpenses(); }
    catch { toast.error("Error"); }
  };


  const handleSettle = async (comment) => {
    const manualAdj = Math.max(0, parseInt(reach || "0", 10) || 0);
    setSettling(true);
    try {
      const res = await authAxios.post(`/events/${eventId}/settle`, { manual_adjustment: manualAdj, comment });
      toast.success(`Event settled. Reach: ${res.data.reach} (${res.data.used_tickets} tickets used${manualAdj ? " + " + manualAdj + " manual" : ""})`);
      setConfirmOpen(false);
      fetchPnl();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSettling(false); }
  };

  const setLineCategory = async (id, category) => {
    try {
      const r = await authAxios.put(`/expenses/${id}/category`, { category });
      setAllExpenses(list => list.map(x => x.id === id ? { ...x, category, ...(r.data.is_capex !== undefined ? { is_capex: r.data.is_capex } : {}) } : x));
      toast.success("Category updated");
      const res = await authAxios.get(`/events/${eventId}/pnl`); setPnl(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update category"); }
  };

  const downloadStatement = async (format) => {
    try {
      const r = await authAxios.get(`/events/${eventId}/pnl/export`, { params: { format }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `PnL_${(pnl.event.name || "event").replace(/[^a-z0-9]+/gi, "_")}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
  };

  const expOpen = !!pnl?.event?.expenses_open;
  const toggleExpenses = async (open) => {
    try {
      await authAxios.put(`/events/${eventId}/expense-submissions`, { open });
      toast.success(open ? "Members and the expense link can now submit expenses" : "Expense submissions closed");
      fetchPnl();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const copyExpenseLink = () => {
    const slug = getTenantSlug?.() || "";
    const prefix = slug ? `/${slug}` : "";
    const url = `${window.location.origin}${prefix}/expense/${eventId}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Expense form link copied!")).catch(() => toast.error("Copy failed"));
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;
  if (!pnl) return null;

  const settled = pnl.event.settlement_status === "settled";
  const finalReach = (pnl.footfall?.used_tickets || 0) + Math.max(0, parseInt(reach || "0", 10) || 0);

  return (
    <div data-testid="event-pnl">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-500 mb-4 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Events</button>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-[#1e3a5f]">{pnl.event.name}</h2>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full ${settled ? "bg-green-100 text-green-600" : "bg-amber-100 text-amber-600"}`}>{settled ? "Settled" : "Open"}</span>
            <button onClick={() => downloadStatement("xlsx")} className="flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium text-[#1e3a5f] hover:bg-slate-50" data-testid="pnl-download-xlsx"><FileSpreadsheet className="w-3 h-3" /> Excel</button>
            <button onClick={() => downloadStatement("pdf")} className="flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium text-[#1e3a5f] hover:bg-slate-50" data-testid="pnl-download-pdf"><Download className="w-3 h-3" /> PDF</button>
          </div>
        </div>
        {!settled && (
          <div className="flex flex-col items-end gap-1.5">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer" data-testid="expense-submissions-toggle-label">
              Expense submissions {expOpen ? "open" : "closed"}
              <Switch checked={expOpen} onCheckedChange={toggleExpenses} data-testid="expense-submissions-toggle" />
            </label>
            {expOpen && <button onClick={copyExpenseLink} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium" data-testid="copy-expense-link-btn"><Copy className="w-3 h-3" /> Expense Link</button>}
          </div>
        )}
      </div>

      {/* P&L Summary */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-green-50 rounded-xl p-3 text-center">
          <p className="text-xl font-extrabold text-green-600">{"\u20ac"}{pnl.revenue.total.toFixed(2)}</p>
          <p className="text-green-600 text-xs">Revenue</p>
        </div>
        <div className="bg-red-50 rounded-xl p-3 text-center">
          <p className="text-xl font-extrabold text-red-500">{"\u20ac"}{pnl.costs.total.toFixed(2)}</p>
          <p className="text-red-500 text-xs">Costs</p>
        </div>
        <div className={`rounded-xl p-3 text-center ${pnl.net_pnl >= 0 ? "bg-green-50" : "bg-red-50"}`}>
          <p className={`text-xl font-extrabold ${pnl.net_pnl >= 0 ? "text-green-700" : "text-red-600"}`}>{"\u20ac"}{pnl.net_pnl.toFixed(2)}</p>
          <p className={`text-xs flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 ${pnl.net_pnl >= 0 ? "text-green-600" : "text-red-500"}`}>
            <span className="whitespace-nowrap">Net P&L</span>
            {pnl.costs.total > 0 && (
              <span className="px-1.5 rounded-full bg-white/70 font-bold whitespace-nowrap" title="Profit / loss as % of costs" data-testid="pnl-net-pct">
                {pnl.net_pnl >= 0 ? "+" : ""}{((pnl.net_pnl / pnl.costs.total) * 100).toFixed(1)}%
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Revenue breakdown */}
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
        <h3 className="font-bold text-sm text-green-700 mb-2">Revenue</h3>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between" data-testid="pnl-ticket-sales"><span className="text-slate-600">Ticket Sales (online)</span><span className="font-medium">&euro;{(pnl.revenue.ticket_sales || 0).toFixed(2)}</span></div>
          <div className="flex justify-between" data-testid="pnl-walkin-sales"><span className="text-slate-600">Walk-in Tickets</span><span className="font-medium">&euro;{(pnl.revenue.walkin_sales || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">Stall / Merchant Sales</span><span className="font-medium">&euro;{(pnl.revenue.stall_sales || 0).toFixed(2)}</span></div>
          <div className="flex justify-between" data-testid="pnl-desk-sales"><span className="text-slate-600">Desk Sales (accountant)</span><span className="font-medium">&euro;{(pnl.revenue.desk_sales || 0).toFixed(2)}</span></div>
          {pnl.revenue.income_items.map(i => (
            <div key={i.id} className="flex justify-between items-center">
              <span className="text-slate-600">{i.description} <span className="text-slate-400 text-xs">{i.date}</span></span>
              <div className="flex items-center gap-1">
                <span className="font-medium">{"\u20ac"}{i.amount.toFixed(2)}</span>
                {!settled && <button onClick={() => handleDeleteIncome(i.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>}
              </div>
            </div>
          ))}
        </div>
        {!settled && (
          showAdd ? (
            <div className="mt-3 bg-green-50 rounded-xl p-3 space-y-2">
              <input value={incomeForm.description} onChange={e => setIncomeForm({...incomeForm, description: e.target.value})} placeholder="Description *" className="w-full px-2 py-1.5 rounded-lg border text-xs" />
              <select value={incomeForm.category} onChange={e => setIncomeForm({...incomeForm, category: e.target.value})} className="w-full px-2 py-1.5 rounded-lg border text-xs">
                <option value="">Select Category *</option>
                {revCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <div className="flex gap-2">
                <input type="number" step="0.01" value={incomeForm.amount} onChange={e => setIncomeForm({...incomeForm, amount: e.target.value})} placeholder="Amount *" className="flex-1 px-2 py-1.5 rounded-lg border text-xs" />
                <input type="date" value={incomeForm.date} onChange={e => setIncomeForm({...incomeForm, date: e.target.value})} className="flex-1 px-2 py-1.5 rounded-lg border text-xs" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowAdd(false)} className="flex-1 py-1.5 rounded-lg border text-xs font-medium">Cancel</button>
                <button onClick={handleAddIncome} className="flex-1 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium">Add</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAdd(true)} className="mt-2 flex items-center gap-1 text-green-600 text-xs font-medium"><Plus className="w-3 h-3" /> Add Income</button>
          )
        )}
      </div>

      {/* Costs breakdown */}
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
        <h3 className="font-bold text-sm text-red-600 mb-2">Costs</h3>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-slate-600">Merchant Payouts</span><span className="font-medium">{"\u20ac"}{pnl.costs.payouts.toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">Expenses</span><span className="font-medium">{"\u20ac"}{pnl.costs.expenses.toFixed(2)}</span></div>
          {(pnl.costs.by_category || []).map(c => (
            <div key={c.name} className="flex justify-between pl-3 text-xs" data-testid={`pnl-cost-cat-${c.name}`}>
              <span className="text-slate-500">{c.name}</span>
              <span className="text-slate-600 tabular-nums">{"\u20ac"}{c.value.toFixed(2)} <span className="text-slate-400">({pnl.costs.expenses > 0 ? Math.round((c.value / pnl.costs.expenses) * 100) : 0}%)</span></span>
            </div>
          ))}
          {pnl.costs.capex > 0 && <div className="flex justify-between text-amber-600"><span>CAPEX (excluded from P&L)</span><span className="font-medium">{"\u20ac"}{pnl.costs.capex.toFixed(2)}</span></div>}
        </div>
        {!settled && (
          showAddExpense ? (
            <div className="mt-3 bg-red-50 rounded-xl p-3 space-y-2">
              <input value={expenseForm.description} onChange={e => setExpenseForm({...expenseForm, description: e.target.value})} placeholder="Description *" className="w-full px-2 py-1.5 rounded-lg border text-xs" />
              <select value={expenseForm.category} onChange={e => setExpenseForm({...expenseForm, category: e.target.value})} className="w-full px-2 py-1.5 rounded-lg border text-xs">
                <option value="">Select Category *</option>
                {expCategories.map(c => <option key={c.id} value={c.name}>{c.name}{c.is_capex ? " (CAPEX)" : ""}</option>)}
              </select>
              <div className="flex gap-2">
                <input type="number" step="0.01" value={expenseForm.amount} onChange={e => setExpenseForm({...expenseForm, amount: e.target.value})} placeholder="Amount *" className="flex-1 px-2 py-1.5 rounded-lg border text-xs" />
                <input type="date" value={expenseForm.date} onChange={e => setExpenseForm({...expenseForm, date: e.target.value})} className="flex-1 px-2 py-1.5 rounded-lg border text-xs" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowAddExpense(false)} className="flex-1 py-1.5 rounded-lg border text-xs font-medium">Cancel</button>
                <button onClick={handleAddDirectExpense} className="flex-1 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium">Add</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAddExpense(true)} className="mt-2 flex items-center gap-1 text-red-600 text-xs font-medium"><Plus className="w-3 h-3" /> Add Expense</button>
          )
        )}
      </div>

      {/* All expenses + merchant payouts with search */}
      {(() => {
        const merchantItems = (pnl.costs.merchant_settlements || []).map(s => ({
          id: s.id, _type: "merchant", submitter_name: s.merchant_name || "Unknown", description: `Merchant Payout`, amount: s.amount, status: "paid", created_at: s.settled_at || "", settled_by: s.settled_by,
        }));
        const combined = [...allExpenses, ...merchantItems];
        const filtered = combined.filter(e => !expSearch || (e.submitter_name || "").toLowerCase().includes(expSearch.toLowerCase()) || (e.description || "").toLowerCase().includes(expSearch.toLowerCase()));
        return combined.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <h3 className="font-bold text-sm text-[#1e3a5f] mb-1">All Expenses & Payouts ({combined.length})</h3>
          <p className="text-[11px] text-slate-500 mb-2" data-testid="pnl-costs-rule">Only approved / paid expenses (excluding CAPEX) and paid merchant payouts count as costs. Pending and rejected claims are listed for reference only.</p>
          <div className="flex gap-2 mb-2">
            <Search className="w-4 h-4 text-slate-400 mt-2" />
            <input value={expSearch} onChange={e => setExpSearch(e.target.value)} placeholder="Search..." className="flex-1 px-2 py-1.5 rounded-lg border text-xs" />
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {filtered.map(e => (
              <div key={e.id} className="border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-2 py-1.5 cursor-pointer hover:bg-slate-50" onClick={() => setExpExpanded(expExpanded === e.id ? null : e.id)}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`px-1 py-0.5 rounded text-[9px] font-bold ${e._type === "merchant" ? "bg-blue-100 text-blue-700" : e.status === "paid" ? "bg-green-100 text-green-700" : e.status === "approved" ? "bg-blue-100 text-blue-700" : e.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{e._type === "merchant" ? "MERCHANT" : e.status}</span>
                    <span className="text-xs truncate">{e.submitter_name} - {e.description}</span>
                  </div>
                  <span className={`ml-auto mr-2 hidden sm:inline px-1.5 py-0.5 rounded-full text-[9px] font-medium whitespace-nowrap ${e._type === "merchant" ? "bg-blue-50 text-blue-600" : e.category ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-600"}`} data-testid={`pnl-line-category-${e.id}`}>
                    {e._type === "merchant" ? "Merchant Payouts" : e.category || "No category"}
                  </span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {e._type !== "merchant" && (["pending", "rejected"].includes(e.status) || e.is_capex) && (
                      <span className="text-[9px] text-slate-400 italic" data-testid={`pnl-not-counted-${e.id}`}>{e.is_capex && !["pending", "rejected"].includes(e.status) ? "CAPEX · not in P&L" : "not in costs"}</span>
                    )}
                    <span className={`text-xs font-bold ${["pending", "rejected"].includes(e.status) && e._type !== "merchant" ? "text-slate-400 line-through decoration-slate-300" : ""}`}>{"\u20ac"}{e.amount.toFixed(2)}</span>
                    <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${expExpanded === e.id ? "rotate-180" : ""}`} />
                  </div>
                </div>
                {expExpanded === e.id && (
                  <div className="bg-slate-50 px-2 pb-2 text-[10px] space-y-0.5 border-t">
                    <div className="pt-1.5 flex items-center gap-2">
                      <strong>Category:</strong>
                      {e._type === "merchant" ? <span>Merchant Payouts</span> : settled ? <span>{e.category || "\u2014"}</span> : (
                        <select value={e.category || ""} onChange={(ev) => ev.target.value && setLineCategory(e.id, ev.target.value)}
                          className="px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[10px]" data-testid={`pnl-category-select-${e.id}`}>
                          <option value="">Select category</option>
                          {expCategories.map(c => <option key={c.id} value={c.name}>{c.name}{c.is_capex ? " (CAPEX)" : ""}</option>)}
                        </select>
                      )}
                    </div>
                    <div className="pt-1 grid grid-cols-2 gap-1">
                      {e.submitter_email && <p><strong>Email:</strong> {e.submitter_email}</p>}
                      {e.payout_method === "wallet" ? (
                        <p><strong>Payout:</strong> Wallet ({e.payout_wallet_id || e.payout_wallet_email || "—"})</p>
                      ) : (e.payout_method === "paypal" || e.iban) ? <RevealPayout expense={e} /> : null}
                      {e.invoice_number && <p><strong>Invoice #:</strong> {e.invoice_number}</p>}
                      {e.invoice_date && <p><strong>Invoice Date:</strong> {e.invoice_date}</p>}
                      {e.created_at && <p><strong>Date:</strong> {new Date(e.created_at).toLocaleDateString()}</p>}
                      {e.approved_at && <p><strong>Approved:</strong> {new Date(e.approved_at).toLocaleDateString()}</p>}
                      {e.paid_at && <p><strong>Paid:</strong> {new Date(e.paid_at).toLocaleDateString()}</p>}
                      {e.settled_by && <p><strong>Settled by:</strong> {e.settled_by}</p>}
                      {e.rejection_reason && <p className="col-span-2 text-red-600"><strong>Reason:</strong> {e.rejection_reason}</p>}
                    </div>
                    {e.source === "direct" && !settled && (
                      <button onClick={() => handleDeleteDirectExpense(e.id)} className="flex items-center gap-1 text-red-500 hover:underline text-[10px]"><Trash2 className="w-3 h-3" /> Remove</button>
                    )}
                    {e.attachment_name && (
                      <button onClick={async () => {
                        try {
                          const r = await fetch(`${API}/expenses/${e.id}/attachment`, {
                            credentials: "include",
                          });
                          if (!r.ok) throw new Error(await r.text());
                          const blob = await r.blob();
                          window.open(URL.createObjectURL(blob), "_blank");
                        } catch { toast.error("Failed"); }
                      }} className="flex items-center gap-1 text-blue-600 hover:underline">
                        <FileText className="w-3 h-3" /> {e.attachment_name}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        );
      })()}

      {/* Reach / Per-person stats */}
      {settled && pnl.event.reach > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <h3 className="font-bold text-sm text-[#1e3a5f] mb-2">Reach & Per-Person</h3>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-slate-50 rounded-xl p-3"><p className="text-xl font-extrabold text-[#1e3a5f]">{pnl.event.reach}</p><p className="text-slate-500 text-xs">Footfall</p></div>
            <div className="bg-green-50 rounded-xl p-3"><p className="text-xl font-extrabold text-green-600">{"\u20ac"}{(pnl.revenue.total / pnl.event.reach).toFixed(2)}</p><p className="text-slate-500 text-xs">Rev / Person</p></div>
            <div className="bg-red-50 rounded-xl p-3"><p className="text-xl font-extrabold text-red-500">{"\u20ac"}{(pnl.costs.total / pnl.event.reach).toFixed(2)}</p><p className="text-slate-500 text-xs">Cost / Person</p></div>
          </div>
        </div>
      )}

      {/* Settle section */}
      {!settled && (
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="font-bold text-sm text-[#1e3a5f] mb-3">Settle Event</h3>
          <div className="mb-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">Auto-computed footfall</p>
            <p className="text-2xl font-extrabold text-[#1e3a5f]" data-testid="auto-footfall">{pnl.footfall?.used_tickets || 0}</p>
            <p className="text-[10px] text-slate-400 mt-1">Tickets scanned or consolidated at the gate. Add a manual adjustment below for walk-ins, paper tickets, or other attendees not captured by ticketing.</p>
          </div>
          <div className="mb-3">
            <label className="text-xs text-slate-500 mb-1 block">Manual adjustment (optional)</label>
            <input type="number" min="0" value={reach} onChange={e => setReach(e.target.value)} placeholder="e.g. 5 walk-ins" className="w-full px-3 py-2.5 rounded-xl border text-sm" data-testid="reach-manual-input" />
            <p className="text-[10px] text-slate-400 mt-1">
              Final reach: <span className="font-semibold text-slate-600" data-testid="reach-total-preview">{(pnl.footfall?.used_tickets || 0) + Math.max(0, parseInt(reach || "0", 10) || 0)}</span>
            </p>
          </div>
          <button onClick={() => setConfirmOpen(true)} className="w-full py-3 rounded-xl bg-[#1e3a5f] text-white font-bold flex items-center justify-center gap-2" data-testid="settle-btn">
            <Lock className="w-5 h-5" /> Settle Event &amp; Lock P&amp;L
          </button>
          <SettleConfirmDialog open={confirmOpen} pnl={pnl} reach={finalReach} busy={settling} onCancel={() => setConfirmOpen(false)} onConfirm={handleSettle} />
        </div>
      )}
      {settled && (
        <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="settlement-info">
          <h3 className="font-bold text-sm text-[#1e3a5f] mb-1 flex items-center gap-1.5"><Lock className="w-4 h-4" /> Settled</h3>
          <p className="text-xs text-slate-500" data-testid="settlement-by">{pnl.event.settled_by || "—"}{pnl.event.settled_at ? ` · ${new Date(pnl.event.settled_at).toLocaleString("de-DE")}` : ""}</p>
          {pnl.event.settlement_comment && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap" data-testid="settlement-comment">“{pnl.event.settlement_comment}”</p>}
        </div>
      )}
    </div>
  );
}
